import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useTranslation } from "react-i18next";
import { useContext } from "react";
import { RestaurantContext } from "src/Utils/Context/RestaurantContext";
import { UserContext } from "src/Utils/Context/UserContext";
import {
  buildNavPath,
  isNavSectionActive,
  isNavSectionDisabled,
  visibleNavSections,
} from "./navBarHelper";

const TEAL = "#2D9B8F";
const TEAL_LIGHT = "rgba(45, 155, 143, 0.12)";
const ICON_INACTIVE = "#6B7280";
const TEXT_INACTIVE = "#374151";

function NavItem({ icon, label, active, disabled, onClick, open: drawerOpen }) {
  return (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
      <ListItemButton
        disabled={disabled}
        onClick={onClick}
        sx={{
          mx: 1,
          borderRadius: 2,
          backgroundColor: active ? TEAL_LIGHT : "transparent",
          ":hover": { backgroundColor: active ? TEAL_LIGHT : "#F9FAFB" },
          minHeight: 44,
        }}
      >
        <ListItemIcon sx={{ minWidth: drawerOpen ? 36 : "auto" }}>
          {icon}
        </ListItemIcon>
        {drawerOpen && (
          <ListItemText
            primary={label}
            primaryTypographyProps={{
              style: {
                fontSize: 14,
                fontWeight: active ? 600 : 400,
                color: active ? TEAL : TEXT_INACTIVE,
              },
            }}
          />
        )}
      </ListItemButton>
    </ListItem>
  );
}

function iconStyle(active) {
  return { color: active ? TEAL : ICON_INACTIVE, fontSize: 20 };
}

export default function SideBarNav({
  leftDrawerOpened,
  restaurantId,
  pathname,
  nav,
}) {
  const { t } = useTranslation();
  const { restaurants, loaded, unsubscribedRestaurants } =
    useContext(RestaurantContext);
  const { user } = useContext(UserContext);

  const selectedRestaurant = restaurantId
    ? restaurants.find((r) => r.id === restaurantId)
    : null;

  const restaurantCapabilities =
    selectedRestaurant?.capabilities.map((c) => c.name) || null;

  // The sections a restaurant-less account keeps are decided once the list has
  // answered — while it is still loading, nothing is hidden.
  const hasRestaurant = !loaded || restaurants.length > 0;

  // Which restaurant the restaurant-bound entries target when the path carries
  // none (the organization and no-restaurant pages): the first one managed, or —
  // for an owner whose subscription lapsed — the one the organization still holds,
  // so the Payments entry leads somewhere instead of sitting greyed out.
  const targetRestaurantId =
    restaurantId ??
    restaurants[0]?.id ??
    unsubscribedRestaurants[0]?.id ??
    null;

  const visibilityContext = {
    owner: user,
    restaurantCapabilities,
    hasRestaurant,
  };
  const matchContext = { pathname };

  return (
    <List sx={{ px: 0, flexGrow: 1, mt: 2 }} disablePadding>
      {visibleNavSections(visibilityContext).map((section) => {
        const Icon = section.icon;
        const active = isNavSectionActive(section, matchContext);

        return (
          <NavItem
            key={section.name}
            open={leftDrawerOpened}
            icon={<Icon sx={iconStyle(active)} />}
            label={t(section.labelKey)}
            active={active}
            disabled={isNavSectionDisabled(section, targetRestaurantId)}
            onClick={nav(buildNavPath(section, targetRestaurantId))}
          />
        );
      })}
    </List>
  );
}
